/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on Jun 21, 2026 by hoarfroster
 *
 */

import { Suspense, useMemo } from 'react';
import { WidgetItem } from '@/contexts/dashboard-context';
import { getWidgetComponent } from '@utils/widget-registry';

interface DashboardWidgetRendererProps {
    item: WidgetItem;
    isEditMode?: boolean;
    isFrozen?: boolean;
    onRequestConfigure?: () => void;
}

export function DashboardWidgetRenderer({
    item,
    isEditMode,
    isFrozen,
    onRequestConfigure,
}: DashboardWidgetRendererProps) {
    const componentId = item.widgetType?.componentId ?? '';
    const Component = useMemo(() => (componentId ? getWidgetComponent(componentId) : null), [componentId]);

    if (!item.widgetType) {
        return (
            <div className="border-theme-badge-yellow bg-theme-warning-soft flex h-full w-full items-center justify-center rounded border-2">
                <div className="text-center">
                    <span className="text-theme-warning font-semibold">Widget Type Not Found</span>
                    <p className="text-theme-text-light mt-1 text-sm">{item.widgetTypeId}</p>
                </div>
            </div>
        );
    }

    if (!Component) {
        return null;
    }

    return (
        <div className="flex h-full w-full flex-col">
            <div className="flex-1 overflow-auto">
                <Suspense
                    fallback={
                        <WidgetLoadingFallback
                            displayName={item.widgetType.displayName ?? item.widgetType.name ?? 'Widget'}
                        />
                    }
                >
                    <Component
                        config={item.config}
                        widgetId={item.id}
                        widgetSize={item.widgetSize}
                        widgetType={item.widgetType}
                        isEditMode={isEditMode}
                        isFrozen={isFrozen}
                        onRequestConfigure={onRequestConfigure}
                    />
                </Suspense>
            </div>
        </div>
    );
}

function WidgetLoadingFallback({ displayName }: { displayName: string }) {
    return (
        <div className="bg-theme-content-bg-light flex h-full w-full flex-col items-center justify-center">
            <div className="border-theme-border border-t-theme-link mb-3 h-10 w-10 animate-spin rounded-full border-4"></div>
            <p className="text-theme-text-muted text-sm">Loading {displayName}...</p>
        </div>
    );
}
